import { useMemo } from 'react';

import { ApolloError } from '@apollo/client';

import {
  Container,
  Screen,
  ScreenContent,
  SessionContext,
} from '../api/generated/types_web';
import { ScreenVariant, useRTEScreen } from '../queries/screen';
import {
  type ScreenViewData,
  buildTransformedScreenData,
} from '../selectors/screenTransforms';

export type { ScreenVariant };

export const ContainerType = {
  media: 'MediaMetadata',
  collection: 'CollectionMetadata',
  teaser: 'PromotionalTeaser',
  content: 'ContentMetadata',
  LinkImage: 'LinkImage',
  LinkText: 'LinkText',
  promo: 'PROMO',
  LiveChannel: 'LiveChannel',
  LiveEvent: 'LiveEvent',
} as const;

/**
 * Query Returns contains and supported elements on screen
 * @param sessionContext
 * @param screenID
 * @param limit
 * @param cursor
 * @returns
 */
export const useScreen = (
  sessionContext: SessionContext,
  screenID: string,
  limit?: number,
  cursor?: null,
  screenVariant?: ScreenVariant,
  enabled = true
): {
  loading: boolean;

  error: ApolloError | Error | undefined;
  screen: Screen | undefined;
  containers: Container[];
  screenContents: ScreenContent[];
  filteredScreenContents: ScreenContent[];
  promoTeaser: Container | undefined;
  containerIds: string[];
  refetch: () => void;
  data: ScreenViewData;
  hasNextPage?: boolean;
  fetchNextPage?: () => Promise<unknown>;
  isFetchingNextPage?: boolean;
} => {
  // `enabled` allows callers to delay the first request until required routing context is ready.
  const {
    loading,
    error,
    screen,
    refetch,
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRTEScreen(sessionContext, screenID, limit, cursor, screenVariant, enabled);

  const fallbackData = useMemo(() => buildTransformedScreenData(screen), [screen]);

  const transformedData = data ?? fallbackData;
  const screenContents = transformedData.screen?.screenContentsPage?.screenContents ?? [];
  const containers = screenContents.filter(
    (content): content is Container => content.__typename === 'Container'
  );
  const containerIds = containers.map((container) => container.id);

  return {
    loading,
    error: error ?? undefined,
    screen,
    containers,
    screenContents,
    filteredScreenContents: transformedData.filteredScreenContents,
    promoTeaser: transformedData.promoTeaser,
    containerIds,
    refetch,
    data: transformedData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};
